import { Plugin, PluginKey } from '@tiptap/pm/state';
import rafThrottle from '../../../utils/lib/rafThrottle';
import { Editor } from '@tiptap/react';
import platformSingleton from '../../../../client/platform/platformSingleton';
import { BrowserEngineType, OperatingSystem } from '../../../platform/platformTypes';
import randomString from '../../../uid/randomString';
import { getChromaColorSafe, getColorSpace, getSimplifiedCSSColorText } from '../../../colors/colorCssUtil';
import { isInlineSwatchPreviewClick } from './inlineSwatchUtils';

/**
 * Opens the colour picker based on the current platform and browser.
 */
const openColorPicker = (colorInput: HTMLInputElement): void => {
    if (!colorInput) return;

    // Desktop Safari needs a frame to be requested before the input can be focused and clicked.
    const shouldWaitFrame =
        platformSingleton.browserEngine === BrowserEngineType.safari && platformSingleton.os === OperatingSystem.macOS;

    if (!shouldWaitFrame) {
        colorInput.focus();
        colorInput.click();
        return;
    }

    requestAnimationFrame(() => {
        const colorInputEl = document.getElementById(colorInput.id) as HTMLInputElement;

        colorInputEl?.focus();
        colorInputEl?.click();
    });
};

/**
 * Triggers a colour picker when clicking on an inline color swatch & replaces the text with the selected color.
 */
export function inlineSwatchClickHandler(editor: Editor): Plugin {
    return new Plugin({
        key: new PluginKey('inlineSwatchClickHandler'),
        props: {
            handleDOMEvents: {
                click(view, event) {
                    if (!isInlineSwatchPreviewClick(event)) return;

                    // Prevent click events when tapping on a swatch colour
                    event.preventDefault();
                    event.stopPropagation();
                    return false;
                },
            },
            handleClick: (view, pos, event) => {
                if (!isInlineSwatchPreviewClick(event)) return;

                const spanElement = event.target as HTMLElement;
                const initialColorStr = spanElement.style.getPropertyValue('--color') || '#000000';

                const colorInput = document.createElement('input');
                colorInput.id = `inline-swatch-input-${randomString(4)}`;
                colorInput.className = 'inline-swatch-input';
                colorInput.type = 'color';

                // Convert the color to a hex format for the input
                const initialChromaColor = getChromaColorSafe(initialColorStr);
                const hexColor = initialChromaColor.hex('rgb');

                colorInput.value = hexColor;

                const spanBoundingRect = (event.target as HTMLElement).getBoundingClientRect();

                // Position underneath the swatch
                colorInput.style.top = `${spanBoundingRect.bottom - 10}px`;
                colorInput.style.left = `${spanBoundingRect.left}px`;

                const abortController = new AbortController();

                /**
                 * Convert the HEX colour value to the appropriate format and update the text in the editor.
                 */
                const updateText = (colorValue: string) => {
                    // Colour hasn't changed, so no need to update
                    if (colorValue === hexColor) return;

                    const initialColorSpace = getColorSpace(initialColorStr);
                    const colorText = getSimplifiedCSSColorText(
                        colorValue,
                        initialColorSpace,
                        initialChromaColor.alpha(),
                    );

                    // Update the text of the span with the new color value
                    const from = parseInt(spanElement.getAttribute('data-from') || '0', 10);
                    const to = parseInt(spanElement.getAttribute('data-to') || '0', 10);

                    // Unable to update if we don't have a valid range
                    if (!from || !to || from > to) return;

                    const tr = view.state.tr.insertText(colorText, from, to);
                    view.dispatch(tr);
                };
                const throttledUpdateText = rafThrottle(updateText);

                const onChange = (event: Event) => throttledUpdateText((event.target as HTMLInputElement)?.value);

                colorInput.addEventListener('input', onChange, { signal: abortController.signal });
                colorInput.addEventListener('change', onChange, { signal: abortController.signal });

                /**
                 * Update the text and remove the colour input from the DOM when it loses focus or is cancelled.
                 */
                const onDestroy = () => {
                    // Remove the colour input from the DOM when it loses focus
                    document.body.removeChild(colorInput);
                    abortController.abort();

                    // Ensure the update gets saved immediately - to improve the UX of undo / redo
                    editor.commands.flushPendingUpdate({});
                };

                // Cross-browser support for handling the destruction of the colour input
                document.addEventListener('pointerdown', onDestroy, { signal: abortController.signal });
                document.addEventListener('mousedown', onDestroy, { signal: abortController.signal });
                document.addEventListener('keydown', onDestroy, { signal: abortController.signal });

                document.body.appendChild(colorInput);

                openColorPicker(colorInput);

                return true;
            },
        },
    });
}
